import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const scrollTo = (e, id) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const polaroids = [
  {
    id: "flowers",
    src: "/hero-flowers.jpg",
    alt: "Gift boxes with fresh flowers",
    className: "absolute top-0 right-2 md:right-6 w-[62%] origin-top-right z-10",
    rotate: -4,
    initial: { opacity: 0, y: 24, rotate: -3 },
    delay: 0.2,
  },
  {
    id: "wrapping",
    src: "/hero-wrapping.jpg",
    alt: "Hands wrapping a gift by hand",
    className: "absolute bottom-2 left-0 w-[52%] origin-bottom-left z-20",
    rotate: 5,
    initial: { opacity: 0, y: 36, rotate: 6 },
    delay: 0.45,
  },
  {
    id: "boxes",
    src: "/hero-boxes.jpg",
    alt: "White gift boxes with blush ribbons",
    className: "absolute bottom-[4%] right-0 w-[42%] origin-bottom-right z-[15]",
    rotate: 3,
    initial: { opacity: 0, y: 30, rotate: 1 },
    delay: 0.6,
  },
];

function SideLabels() {
  return (
    <>
      <div
        aria-hidden
        className="hidden xl:flex absolute left-6 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-10 select-none"
      >
        <span
          className="overline text-[10px] text-[#8B4513]/70"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Est. India · Bespoke Gifting
        </span>
        <span className="w-px h-24 bg-[#8B4513]/30" />
        <span className="overline text-[10px] text-[#8B4513]/60">GC</span>
      </div>

      <div className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-10 select-none">
        <span className="overline text-[10px] text-[#8B4513]/60">07</span>
        <span className="w-px h-24 bg-[#8B4513]/30" />
        <span
          className="overline text-[10px] text-[#8B4513]/70"
          style={{ writingMode: "vertical-rl" }}
        >
          Steps of crafting one gift
        </span>
      </div>
    </>
  );
}

function HeroHeadline() {
  return (
    <>
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        data-testid="hero-overline"
        className="overline text-[#8B4513] mb-7 inline-flex items-center gap-3"
      >
        <span className="w-8 h-px bg-[#8B4513]/60" />
        The Art of Gifting
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.95, delay: 0.1 }}
        data-testid="hero-title"
        className="font-serif-display text-[2.8rem] sm:text-5xl md:text-6xl lg:text-[5.4rem] xl:text-[6rem] leading-[1.02] tracking-[-0.015em] text-[#2A1D15]"
      >
        Gifts, turned
        <br />
        into{" "}
        <span className="italic text-[#8B4513]">cherished</span>
        <br />
        <span className="italic text-[#8B4513]">memories.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mt-7 max-w-lg text-base md:text-lg text-[#5C4334] font-light leading-[1.85]"
      >
        An end-to-end concierge for thoughtful, bespoke gifting. We ideate,
        curate and hand-package the perfect gift for every occasion — so
        what you give becomes a moment they hold onto.
      </motion.p>
    </>
  );
}

function HeroCtas() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.5 }}
      className="mt-9 flex flex-wrap items-center gap-4"
    >
      <a
        href="#gallery"
        data-testid="hero-cta-gallery"
        onClick={(e) => scrollTo(e, "gallery")}
        className="bg-[#a05a26] hover:bg-[#8B4513] text-[#FDFBF7] px-9 py-5 rounded-full overline text-[11px] transition-colors duration-300"
      >
        Explore our work
      </a>
      <a
        href="#contact"
        data-testid="hero-cta-contact"
        onClick={(e) => scrollTo(e, "contact")}
        className="border border-[#8B4513]/70 text-[#8B4513] hover:bg-[#8B4513] hover:text-[#FDFBF7] px-9 py-5 rounded-full overline text-[11px] transition-colors duration-300"
      >
        Start your gift
      </a>
    </motion.div>
  );
}

function TrustStrip() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.75 }}
      className="mt-12 flex items-center gap-7 md:gap-10 flex-wrap"
    >
      <div>
        <p className="font-serif-display text-3xl md:text-4xl text-[#2A1D15] leading-none">
          100<span className="text-[#8B4513]">+</span>
        </p>
        <p className="overline text-[10px] text-[#5C4334] mt-2">
          Gifts crafted
        </p>
      </div>
      <span className="hidden sm:block w-px h-10 bg-[#8B4513]/25" />
      <div>
        <p className="font-serif-display text-3xl md:text-4xl text-[#2A1D15] leading-none italic">
          30<span className="text-[#8B4513]">+</span>
        </p>
        <p className="overline text-[10px] text-[#5C4334] mt-2">
          Occasions covered
        </p>
      </div>
      <span className="hidden sm:block w-px h-10 bg-[#8B4513]/25" />
      <div className="inline-flex items-center gap-2 text-[#8B4513]">
        <Sparkles size={16} strokeWidth={1.5} />
        <p className="overline text-[10px]">Made-to-order, always</p>
      </div>
    </motion.div>
  );
}

function HeroCopy() {
  return (
    <div className="lg:col-span-7">
      <HeroHeadline />
      <HeroCtas />
      <TrustStrip />
    </div>
  );
}

function Polaroid({ config }) {
  return (
    <motion.div
      initial={config.initial}
      animate={{ opacity: 1, y: 0, rotate: config.rotate }}
      transition={{ duration: 1.1, delay: config.delay, ease: [0.22, 1, 0.36, 1] }}
      className={`polaroid aspect-[3/4] ${config.className}`}
      style={{ transform: `rotate(${config.rotate}deg)` }}
    >
      <img
        src={config.src}
        alt={config.alt}
        className="w-full h-full object-cover"
      />
    </motion.div>
  );
}

function QuoteChip() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
      animate={{ opacity: 1, scale: 1, rotate: -6 }}
      transition={{ duration: 1, delay: 0.7 }}
      className="absolute -top-2 -left-2 md:-top-4 md:-left-6 z-30 bg-[#fdfbf7] px-5 py-3 shadow-[0_18px_40px_-22px_rgba(42,29,21,0.4)] max-w-[210px]"
      style={{ transform: "rotate(-6deg)" }}
    >
      <p className="font-serif-display italic text-base md:text-lg text-[#2A1D15] leading-snug">
        "It's the little things that matter."
      </p>
    </motion.div>
  );
}

function HeroCollage() {
  return (
    <div className="lg:col-span-5 relative">
      <div className="relative h-[460px] md:h-[600px] lg:h-[640px]">
        {polaroids.map((p) => (
          <Polaroid key={p.id} config={p} />
        ))}
        <QuoteChip />
        <motion.img
          initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.85 }}
          src="/logo.png"
          alt="Gifting Concierge"
          className="floaty hidden md:block absolute -bottom-2 right-0 w-[110px] lg:w-[125px] h-auto z-30"
        />
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center bg-[#ebe0cd] overflow-hidden pt-32 md:pt-36 pb-20"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-[#f4ead7]/80 via-transparent to-[#d8c8ad]/40 pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#d19c72]/15 blur-3xl"
      />

      <SideLabels />

      <div className="relative z-10 max-w-[1340px] mx-auto w-full px-6 md:px-10 lg:px-16 xl:px-24 grid lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-center">
        <HeroCopy />
        <HeroCollage />
      </div>
    </section>
  );
}
